import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

const WhyPatientsLove = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      title: t('whyPatientsLove.feature1Title'),
      description: t('whyPatientsLove.feature1Description'),
      icon: "/assets/cards1.svg",
      bgIcon: "/assets/card1.svg",
      bgColor: "bg-[#007B83]",
      textColor: "text-white",
      descriptionColor: "text-white"
    },
    {
      title: t('whyPatientsLove.feature2Title'),
      description: t('whyPatientsLove.feature2Description'),
      icon: "/assets/cards2.svg",
      bgIcon: "/assets/card2.svg",
      bgColor: "bg-[#E9F9FA]",
      textColor: "text-[#1A1A1A]",
      descriptionColor: "text-[#1A1A1A]"
    },
    {
      title: t('whyPatientsLove.feature3Title'),
      description: t('whyPatientsLove.feature3Description'),
      icon: "/assets/cards3.svg",
      bgIcon: "/assets/card3.svg",
      bgColor: "bg-[#1C99DE]",
      textColor: "text-white",
      descriptionColor: "text-white"
    },
    {
      title: t('whyPatientsLove.feature4Title'),
      description: t('whyPatientsLove.feature4Description'),
      icon: "/assets/cards4.svg",
      bgIcon: "/assets/card4.svg",
      bgColor: "bg-[#E3F3FA]",
      textColor: "text-[#1A1A1A]",
      descriptionColor: "text-[#1A1A1A]"
    }
  ];

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const trustVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut", delay: 0.6 },
    },
  };

  return (
    <section ref={ref} className="bg-[#FAFAFA] py-16 md:py-24">
      <div className="container-custom">
        {/* Top Section */}
        <motion.div
          className="text-center mb-12 md:mb-16"
          variants={headerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="text-sm text-[#5A5A59] mb-4">
            {t('whyPatientsLove.badge')}
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold bg-gradient-to-r from-[#000087] to-[#000000] bg-clip-text text-transparent mb-6 leading-tight max-w-[666px] mx-auto">
            {t('whyPatientsLove.heading')}
          </h2>
          
          <p className="text-base md:text-lg text-[#000929] max-w-xl mx-auto leading-relaxed">
            {t('whyPatientsLove.description')}
          </p>
        </motion.div>

        {/* Feature Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className={`${feature.bgColor} rounded-2xl p-6 md:p-8 relative overflow-hidden`}
              variants={cardVariants}
            >
              {/* Background Icon - Top Right */}
              <div className="absolute top-0 right-0">
                <img 
                  src={feature.bgIcon} 
                  alt="" 
                  className="object-contain"
                />
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className="mb-6">
                  {feature.bgColor === "bg-[#E9F9FA]" ? (
                    <div className="">
                      <img src={feature.icon} alt={feature.title} />
                    </div>
                  ) : (
                    <div className="">
                      <img src={feature.icon} alt={feature.title} />
                    </div>
                  )}
                </div>

                {/* Title */}
                <h3 className={`text-xl md:text-2xl font-bold ${feature.textColor} mb-3 leading-tight`}>
                  {feature.title}
                </h3>

                {/* Description */}
                <p className={`text-sm md:text-base ${feature.descriptionColor} leading-relaxed`}>
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Trust Statement */}
        <motion.div
          className="flex items-center justify-center gap-2 text-[#5A5A59]"
          variants={trustVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <img src="/assets/checks.svg" alt="Verified" className="w-5 h-5" />
          <span className="text-sm md:text-base">
            {t('whyPatientsLove.trustStatement')}
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyPatientsLove;

